import { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * ThemeContext · Sprint V2 wyvio
 * ─────────────────────────────
 * Le thème clair reste l'identité par défaut de wyvio.
 * Le mode sombre est une PRÉFÉRENCE UTILISATEUR (Réglages → Apparence).
 *
 * Valeurs stockées : "light" | "dark" | "auto"
 * - light : forcé clair
 * - dark  : forcé sombre
 * - auto  : suit `prefers-color-scheme` du système (comportement Apple / macOS)
 *
 * Application : ajout / retrait de la classe `dark` sur <html>. La palette
 * dark est activée via `dark:` (Tailwind darkMode='class', déjà configuré).
 */

const STORAGE_KEY = "wyvio.theme";
const VALID = new Set(["light", "dark", "auto"]);

const ThemeContext = createContext({
  preference: "light",
  effective: "light",
  setPreference: () => {},
});

const readSaved = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return VALID.has(v) ? v : "light";
  } catch {
    return "light";
  }
};

const systemPrefersDark = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-color-scheme: dark)").matches;

export const ThemeProvider = ({ children }) => {
  const [preference, setPreferenceState] = useState(readSaved);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  // Écoute le changement de préférence système (mode auto)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => setSystemDark(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);

  const effective =
    preference === "auto" ? (systemDark ? "dark" : "light") : preference;

  // Applique la classe sur <html>
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = effective;
    root.classList.toggle("dark", effective === "dark");
  }, [effective]);

  const setPreference = useCallback((v) => {
    const val = VALID.has(v) ? v : "light";
    setPreferenceState(val);
    try {
      localStorage.setItem(STORAGE_KEY, val);
    } catch {
      /* localStorage inaccessible — le choix est perdu, pas grave */
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ preference, effective, setPreference }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
